import {colors} from 'shells/colors/colors';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';
import {OnboardingModal} from 'shells/onboarding-modal/onboarding-modal';
import {OnboardingBody} from 'shells/onboarding-page/onboarding-body';

import {useOpenableModal, OpenableModals} from '../../openable-modals';

const REUSE_AND_COPY_QUOTAS_PAGE = {
    id: 'reuse-and-copy-quotas',
    body: (
        <OnboardingBody title='Set once, reuse across your team' statusText='New: Sales quotas'>
            <p>
                Create monthly targets for one user, then copy those quotas to others to quickly set
                up your team for the fiscal year.
            </p>
        </OnboardingBody>
    ),
    art: {
        background: colors.blueXlt,
        image: () => <Illustration src='quotas/reuse-and-copy-quotas' width={400} height={588} />,
    },
};

const PAGES_SUCCESSFUL_MIGRATION = [
    {
        id: 'individual-quotas',
        body: (
            <OnboardingBody
                title='Quotas, redesigned for individuals'
                statusText='New: Sales quotas'
            >
                <p>
                    Quotas in Nutshell are now based on individual users instead of teams for more
                    flexibility and control.{' '}
                </p>
                <p>
                    Your existing quotas have been automatically migrated to the new system –
                    reporting and quota tracking works exactly the same.
                </p>
            </OnboardingBody>
        ),
        art: {
            background: colors.blueXlt,
            image: () => <Illustration src='quotas/individual-quotas' width={400} height={588} />,
        },
    },
    REUSE_AND_COPY_QUOTAS_PAGE,
];

const PAGES_UNSUCCESSFUL_MIGRATION = [
    {
        id: 'individual-quotas',
        body: (
            <OnboardingBody
                title='Quotas, redesigned for individuals'
                statusText='New: Sales quotas'
            >
                <p>
                    We've moved from team-wide quotas to individual goals. Assign targets directly
                    to each user for more flexibility and control.
                </p>
            </OnboardingBody>
        ),
        art: {
            background: colors.blueXlt,
            image: () => <Illustration src='quotas/individual-quotas' width={400} height={588} />,
        },
    },
    {
        id: 'some-quotas-need-review',
        body: (
            <OnboardingBody title='Some quotas need your review' statusText='New: Sales quotas'>
                <p>
                    You’ve configured team-based quotas that cannot be automatically migrated to the
                    new quota system.
                </p>
                <p>
                    Remove these quotas and set new ones for each user if you’d like additional
                    flexibility in setting individual quotas.
                </p>
                <p>
                    Need help with the transition?
                    <br />
                    <Link href='https://support.nutshell.com' newTab={true} variant='primary'>
                        Contact our support team
                    </Link>
                </p>
            </OnboardingBody>
        ),
        art: {
            background: colors.blueXlt,
            image: () => (
                <Illustration src='quotas/some-quotas-need-review' width={400} height={588} />
            ),
        },
    },
];

export function QuotasV2OnboardingModal() {
    const {isOpen, onClose: onOpenableModalClose} = useOpenableModal(
        OpenableModals.QUOTAS_V2_ONBOARDING_MODAL
    );

    const handleClose = () => {
        onOpenableModalClose();
    };

    if (!isOpen) {
        return null;
    }

    return (
        <OnboardingModal
            lastButtonText='Done'
            pages={
                NutClientConfig.hasMigratedToQuotasV2
                    ? PAGES_SUCCESSFUL_MIGRATION
                    : PAGES_UNSUCCESSFUL_MIGRATION
            }
            onClose={handleClose}
            onFinish={handleClose}
            maxHeight={600}
        />
    );
}
